import React, { useEffect, useRef, useState } from 'react';
import type { WorkflowInstanceViewProps } from 'interfaces/workflows/WorkflowInstanceViewProps';
import { NodeInstanceStatus } from 'models/nodes/NodeInstanceStatus';
import { NodeType } from 'models/nodes/NodeType';
import type { Enrollment } from 'models/enrollments/Enrollment';
import { ReviewEnrollment } from 'models/enrollments/ReviewEnrollment';
import ReviewEnrollmentView from 'components/enrollments/ReviewEnrollmentView';
import EnrollmentView from 'components/enrollments/EnrollmentView';
import type { NodeInstance } from 'models/nodes/NodeInstance';
import type { ReviewDecisionSubmission } from 'models/submissions/ReviewDecisionSubmission';
import { ApiService } from 'services/ApiService';
import type { Form } from 'models/forms/Form';
import { FormViewer } from 'components/forms/FormViewer';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<NodeInstanceStatus, string> = {
  [NodeInstanceStatus.Pending]: 'bg-gray-100 text-gray-600 border border-gray-300',
  [NodeInstanceStatus.Active]: 'bg-blue-100 text-blue-700 border border-blue-400',
  [NodeInstanceStatus.Waiting]: 'bg-yellow-100 text-yellow-700 border border-yellow-400',
  [NodeInstanceStatus.Completed]: 'bg-green-100 text-green-700 border border-green-400',
  [NodeInstanceStatus.Failed]: 'bg-red-100 text-red-700 border border-red-400',
};

const STATUS_DOT: Record<NodeInstanceStatus, string> = {
  [NodeInstanceStatus.Pending]: 'bg-gray-400',
  [NodeInstanceStatus.Active]: 'bg-blue-500',
  [NodeInstanceStatus.Waiting]: 'bg-yellow-500',
  [NodeInstanceStatus.Completed]: 'bg-green-500',
  [NodeInstanceStatus.Failed]: 'bg-red-500',
};

const STATUS_LABEL: Record<NodeInstanceStatus, string> = {
  [NodeInstanceStatus.Pending]: 'Pending',
  [NodeInstanceStatus.Active]: 'Active',
  [NodeInstanceStatus.Waiting]: 'Waiting',
  [NodeInstanceStatus.Completed]: 'Completed',
  [NodeInstanceStatus.Failed]: 'Failed',
};

const NODE_TYPE_LABEL: Partial<Record<NodeType, string>> = {
  [NodeType.Form]: 'Form',
  [NodeType.TeamReview]: 'Team Review',
  [NodeType.AutomatedAction]: 'Automated',
  [NodeType.Consultation]: 'Consultation',
};

const isActionable = (status: NodeInstanceStatus) =>
  status === NodeInstanceStatus.Active || status === NodeInstanceStatus.Waiting;

const formatDate = (iso?: string): string | null =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

const prettyJson = (raw?: string): string => {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

interface TeamReviewOutput {
  decision: string;
  enrollmentId: string | null;
}

const parseTeamReviewOutput = (outputJson?: string): TeamReviewOutput | null => {
  if (!outputJson) return null;
  try {
    const parsed = JSON.parse(outputJson) as Record<string, unknown>;
    const decision = typeof parsed['Decision'] === 'string' ? parsed['Decision'] : null;
    const enrollmentId = typeof parsed['EnrollmentId'] === 'string' ? parsed['EnrollmentId'] : null;
    if (!decision) return null;
    return { decision, enrollmentId };
  } catch {
    return null;
  }
};

const REVIEW_DECISION_BADGE: Record<string, string> = {
  Approved: 'bg-green-100 text-green-700 border border-green-300',
  RequestChanges: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
};

const REVIEW_DECISION_LABEL: Record<string, string> = {
  Approved: 'Approved',
  RequestChanges: 'Changes Requested',
  Rejected: 'Denied',
};

// ---------------------------------------------------------------------------
// CompletedTeamReviewPanel
//
// Renders the finished team review (all votes, final decision) for a completed
// TeamReview node. Reuses ReviewEnrollmentView which detects isFinished and
// automatically shows the read-only summary.
// ---------------------------------------------------------------------------

interface CompletedTeamReviewPanelProps {
  enrollmentId: string;
  node: NodeInstance;
  teamId: string;
}

const CompletedTeamReviewPanel: React.FC<CompletedTeamReviewPanelProps> = ({ enrollmentId, node, teamId }) => {
  const reviewEnrollment = new ReviewEnrollment(enrollmentId, teamId);
  return (
    <ReviewEnrollmentView
      enrollment={reviewEnrollment}
      nodeInstance={node}
      isDisabled={true}
    />
  );
};

// Fetches the form definition, merges submitted values from outputJson, renders read-only.
interface CompletedFormPanelProps {
  node: NodeInstance;
  teamId: string;
}

const CompletedFormPanel: React.FC<CompletedFormPanelProps> = ({ node, teamId }) => {
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!node.formId) {
      setError('Form definition is not available for this step.');
      setLoading(false);
      return;
    }

    // Short-circuit: the frontend serialises the full UserSubmissionDto — including the
    // Form snapshot with each element's answered value — into responseJson on submit.
    // FormNodeHandler unwraps and stores that as outputJson. If the snapshot is present,
    // use it directly and skip the API round-trip entirely.
    if (node.outputJson) {
      try {
        const parsed = JSON.parse(node.outputJson) as Record<string, unknown>;
        // Handle legacy { responseJson: "<json string>" } wrapper
        const source: Record<string, unknown> =
          typeof parsed['responseJson'] === 'string'
            ? (JSON.parse(parsed['responseJson'] as string) as Record<string, unknown>)
            : parsed;

        const rawSubmission = source['userSubmission'] as Record<string, unknown> | undefined;
        const embeddedForm = rawSubmission?.['form'] as Form | undefined;
        if (embeddedForm?.id && Array.isArray(embeddedForm.elements)) {
          setForm(embeddedForm);
          setLoading(false);
          return;
        }
      } catch { /* fall through to API fetch */ }
    }

    // Fallback: fetch the form definition from the API and merge root-key answers.
    // Handles older outputJson shapes that do not embed the full form snapshot.
    let cancelled = false;
    ApiService.getForm(teamId, node.formId)
      .then((response) => {
        if (cancelled) return;
        const formDef = response.data;
        if (!formDef) { setError('Form not found.'); return; }
        // Parse outputJson into a flat answers map (key = element id or name)
        const answers: Record<string, string> = {};
        if (node.outputJson) {
          try {
            const parsed = JSON.parse(node.outputJson) as Record<string, unknown>;
            const source =
              typeof parsed['responseJson'] === 'string'
                ? (JSON.parse(parsed['responseJson'] as string) as Record<string, unknown>)
                : parsed;
            Object.entries(source).forEach(([k, v]) => {
              answers[k] = v == null ? '' : String(v);
            });
          } catch { /* render form without pre-filled values */ }
        }
        // Merge answers into form elements by id, falling back to name
        const filledElements = formDef.elements.map((el) => ({
          ...el,
          value: answers[el.id] ?? answers[el.name] ?? el.value,
        }));
        setForm({ ...formDef, elements: filledElements } as Form);
      })
      .catch(() => { if (!cancelled) setError('Could not load the form.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [node.formId, node.outputJson, teamId]);

  if (loading) {
    return (
      <div className="space-y-2 py-1" aria-label="Loading form">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-4 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !form) {
    return <p className="text-xs text-red-600">{error ?? 'Form not available.'}</p>;
  }

  return (
    <FormViewer
      formId={node.formId ?? ''}
      form={form}
      isDisabled={true}
      teamId={teamId}
    />
  );
};

// ---------------------------------------------------------------------------
// ActiveNodePanel
//
// Rendered for every active/waiting node that has an activeEnrollmentId.
// Fetches the enrollment, then dispatches by the *runtime type* of the
// enrollment object — NOT node.nodeType, which may be absent before the
// backend T9.1–T9.5 deployment.
//
//   ReviewEnrollment  → ReviewEnrollmentView (gated on assignedTeamId)
//   everything else   → EnrollmentView (handles Form, Consultation, etc.)
// ---------------------------------------------------------------------------

interface ActiveNodePanelProps {
  node: NodeInstance;
  teamId: string;
  artistId: string;
  onSubmitReview: (enrollmentId: string, submission: ReviewDecisionSubmission) => Promise<void>;
  isSubmitting: boolean;
  onReviewComplete?: () => void;
}

const ActiveNodePanel: React.FC<ActiveNodePanelProps> = ({
  node,
  teamId,
  artistId,
  onSubmitReview,
  isSubmitting,
  onReviewComplete,
}) => {
  const isTeamReview = node.nodeType === NodeType.TeamReview;

  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [fetchLoading, setFetchLoading] = useState(
    !isTeamReview && !!node.activeEnrollmentId,
  );
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    // TeamReview nodes: ReviewEnrollmentView fetches its own data — no enrollment fetch needed.
    if (isTeamReview || !node.activeEnrollmentId) {
      setFetchLoading(false);
      return;
    }
    let cancelled = false;
    setFetchLoading(true);
    setFetchError(null);
    ApiService.getEnrollment(teamId, node.activeEnrollmentId)
      .then((e) => { if (!cancelled) setEnrollment(e); })
      .catch(() => { if (!cancelled) setFetchError('Could not load enrollment details.'); })
      .finally(() => { if (!cancelled) setFetchLoading(false); });
    return () => { cancelled = true; };
  }, [node.activeEnrollmentId, teamId, isTeamReview]);

  // TeamReview: bypass enrollment fetch; ReviewEnrollmentView manages its own data via getEnrollmentTeamReview.
  if (isTeamReview && node.activeEnrollmentId) {
    const reviewEnrollment = new ReviewEnrollment(node.activeEnrollmentId, teamId);
    const reviewUrl = `${window.location.origin}/artist/${artistId}/teams/${teamId}/reviews/${node.activeEnrollmentId}`;
    return (
      <div className="mt-3 space-y-2">
        <a
          href={reviewUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          aria-label="Open team review in a new tab"
        >
          Open review link
          <span aria-hidden>↗</span>
        </a>
        <ReviewEnrollmentView
          enrollment={reviewEnrollment}
          nodeInstance={node}
          onReviewComplete={onReviewComplete}
          isDisabled={isSubmitting}
        />
      </div>
    );
  }

  if (fetchLoading) {
    return (
      <div className="mt-3 space-y-2" aria-label="Loading enrollment">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        ))}
      </div>
    );
  }

  if (fetchError) {
    return <p className="mt-3 text-xs text-red-600">{fetchError}</p>;
  }

  if (!enrollment) return null;

  if (enrollment instanceof ReviewEnrollment) {
    if (enrollment.assignedTeamId !== teamId) {
      return (
        <p className="mt-3 text-xs text-gray-400 italic">
          This review is assigned to a different team.
        </p>
      );
    }
    return (
      <div className="mt-3">
        <ReviewEnrollmentView
          enrollment={enrollment}
          nodeInstance={node}
          onReviewComplete={onReviewComplete}
          isDisabled={isSubmitting}
        />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <EnrollmentView
        enrollment={enrollment}
        teamId={teamId}
        isDisabled={false}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// WorkflowInstanceView
// ---------------------------------------------------------------------------

export const WorkflowInstanceView: React.FC<WorkflowInstanceViewProps> = ({
  workflowInstance,
  teamId,
  artistId,
  onSubmitReview,
  isSubmitting,
  onReviewComplete,
}) => {
  const sorted = [...workflowInstance.nodeInstances].sort((a, b) => a.order - b.order);

  // Track which completed nodes have their output panel open
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Auto-scroll to the first active / waiting node on open
  const activeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [workflowInstance.id]);

  return (
    <div className="px-5 py-4">
      <ol className="relative space-y-0" aria-label="Workflow steps">
        {sorted.map((node, index) => {
          const isLast = index === sorted.length - 1;
          const isCompleted = node.status === NodeInstanceStatus.Completed;
          const isActive = isActionable(node.status);
          const isExpanded = expandedIds.has(node.id);
          const nodeLabel =
            (node.nodeType ? NODE_TYPE_LABEL[node.nodeType] : undefined) ?? `Step ${node.order}`;

          return (
            <li key={node.id} className="relative flex gap-4">
              {/* Vertical rail */}
              {!isLast && (
                <div
                  className="absolute left-[19px] top-8 bottom-0 w-px bg-gray-200"
                  aria-hidden="true"
                />
              )}

              {/* Timeline dot */}
              <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center">
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ring-2 ${
                    isActive
                      ? 'bg-blue-500 ring-blue-300'
                      : isCompleted
                      ? 'bg-green-500 ring-green-300'
                      : node.status === NodeInstanceStatus.Failed
                      ? 'bg-red-500 ring-red-300'
                      : 'bg-gray-300 ring-gray-200'
                  }`}
                  aria-hidden="true"
                >
                  {isCompleted && (
                    <svg
                      className="h-3 w-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
              </div>

              {/* Row content */}
              <div
                ref={isActive ? activeRef : undefined}
                className="flex-1 pb-6"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Step {node.order} — {nodeLabel}
                    </p>
                    {node.activatedAt && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Started {formatDate(node.activatedAt)}
                        {node.completedAt && ` · Completed ${formatDate(node.completedAt)}`}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[node.status]}`}
                    >
                      <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${STATUS_DOT[node.status]}`}
                        aria-hidden="true"
                      />
                      {STATUS_LABEL[node.status]}
                    </span>

                    {/* Inline decision badge for completed team review nodes */}
                    {isCompleted && node.nodeType === NodeType.TeamReview && (() => {
                      const reviewOutput = parseTeamReviewOutput(node.outputJson);
                      if (!reviewOutput) return null;
                      const badgeCls = REVIEW_DECISION_BADGE[reviewOutput.decision] ?? 'bg-gray-100 text-gray-600 border border-gray-300';
                      const label = REVIEW_DECISION_LABEL[reviewOutput.decision] ?? reviewOutput.decision;
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeCls}`}>
                          {label}
                        </span>
                      );
                    })()}

                    {isCompleted && (() => {
                      if (node.nodeType === NodeType.TeamReview) {
                        const reviewOutput = parseTeamReviewOutput(node.outputJson);
                        if (!reviewOutput?.enrollmentId) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(node.id)}
                            aria-expanded={isExpanded}
                            className="text-xs text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                          >
                            {isExpanded ? 'Hide Team Review' : 'View Team Review'}
                          </button>
                        );
                      }
                      if (!node.outputJson) return null;
                      return (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(node.id)}
                          aria-expanded={isExpanded}
                          className="text-xs text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                        >
                          {isExpanded
                            ? (node.nodeType === NodeType.Form ? 'Hide form' : 'Hide output')
                            : (node.nodeType === NodeType.Form ? 'View form' : 'View output')
                          }
                        </button>
                      );
                    })()}
                  </div>
                </div>

                {/* Output panel for completed nodes */}
                {isCompleted && isExpanded && (() => {
                  if (node.nodeType === NodeType.TeamReview) {
                    const reviewOutput = parseTeamReviewOutput(node.outputJson);
                    if (!reviewOutput?.enrollmentId) return null;
                    return (
                      <CompletedTeamReviewPanel
                        enrollmentId={reviewOutput.enrollmentId}
                        node={node}
                        teamId={teamId}
                      />
                    );
                  }
                  if (!node.outputJson) return null;
                  return (
                    <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 p-3">
                      {node.nodeType === NodeType.Form
                        ? <CompletedFormPanel node={node} teamId={teamId} />
                        : (
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono">
                            {prettyJson(node.outputJson)}
                          </pre>
                        )
                      }
                    </div>
                  );
                })()}

                {/* Enrollment panel — fetches and dispatches by runtime enrollment type */}
                {isActive && node.activeEnrollmentId && (
                  <ActiveNodePanel
                    node={node}
                    teamId={teamId}
                    artistId={artistId}
                    onSubmitReview={onSubmitReview}
                    isSubmitting={isSubmitting}
                    onReviewComplete={onReviewComplete}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default WorkflowInstanceView;
